import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash2, X, Star } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p) => `${BASE}${p.url || `/api/job-photos/${p.id}`}`;

export function JobPhotosManager({ appt }) {
  const [photos, setPhotos] = useState(appt.job_photos || []);
  const [busy, setBusy] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const upload = async (files, label) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", label);
        const r = await api.post(`/admin/appointments/${appt.id}/photos`, fd);
        setPhotos((p) => [...p, r.data]);
      }
      toast.success("Photo(s) uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pid) => {
    try {
      await api.delete(`/admin/appointments/${appt.id}/photos/${pid}`);
      setPhotos((p) => p.filter((x) => x.id !== pid));
    } catch {
      toast.error("Couldn't delete the photo — please try again.");
    }
  };

  const toggleFeatured = async (p) => {
    try {
      await api.patch(`/admin/appointments/${appt.id}/photos/${p.id}`, { featured: !p.featured });
      setPhotos((list) => list.map((x) => (x.id === p.id ? { ...x, featured: !p.featured } : x)));
      toast.success(!p.featured ? "Added to the public highlights wall" : "Removed from highlights");
    } catch {
      toast.error("Couldn't update the photo");
    }
  };

  const saveCaption = async (p, caption) => {
    if ((p.caption || "") === caption) return;
    try {
      await api.patch(`/admin/appointments/${appt.id}/photos/${p.id}`, { caption });
      setPhotos((list) => list.map((x) => (x.id === p.id ? { ...x, caption } : x)));
    } catch {
      toast.error("Couldn't save the caption");
    }
  };

  return (
    <div data-testid={`job-photos-manager-${appt.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Before / after photos</span>
        <div className="flex gap-2">
          <input ref={beforeRef} type="file" accept="image/*" multiple hidden
            onChange={(e) => { upload(Array.from(e.target.files), "before"); e.target.value = ""; }} />
          <input ref={afterRef} type="file" accept="image/*" multiple hidden
            onChange={(e) => { upload(Array.from(e.target.files), "after"); e.target.value = ""; }} />
          <button type="button" data-testid={`add-before-photo-${appt.id}`} disabled={busy} onClick={() => beforeRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-700 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:border-brand-red hover:text-brand-red disabled:opacity-50">
            <Camera className="h-3 w-3" /> Before
          </button>
          <button type="button" data-testid={`add-after-photo-${appt.id}`} disabled={busy} onClick={() => afterRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-700 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:border-brand-red hover:text-brand-red disabled:opacity-50">
            <Camera className="h-3 w-3" /> After
          </button>
        </div>
      </div>
      {busy && <div className="mt-2 text-[11px] font-mono text-zinc-500">Uploading…</div>}
      {photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.id} className="w-24" data-testid={`job-photo-${p.id}`}>
              <div className="relative group">
                <a href={photoUrl(p)} target="_blank" rel="noreferrer">
                  <img src={photoUrl(p)} alt={p.label} loading="lazy"
                    className={`h-24 w-24 object-cover border ${p.featured ? "border-brand-red" : "border-zinc-800 group-hover:border-brand-red"}`} />
                </a>
                <span className="absolute bottom-0 left-0 px-1 py-0.5 bg-black/80 text-[8px] font-mono uppercase tracking-widest text-zinc-300">{p.label}</span>
                <button type="button" data-testid={`feature-job-photo-${p.id}`} onClick={() => toggleFeatured(p)}
                  title={p.featured ? "Remove from public highlights wall" : "Feature on public highlights wall"}
                  className={`absolute top-0.5 left-0.5 h-5 w-5 grid place-items-center bg-black/70 ${p.featured ? "text-yellow-400" : "text-zinc-500 hover:text-yellow-400"}`}>
                  <Star className="h-3 w-3" fill={p.featured ? "currentColor" : "none"} />
                </button>
                <button type="button" data-testid={`delete-job-photo-${p.id}`} onClick={() => remove(p.id)} aria-label="Delete photo"
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 grid place-items-center bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-brand-red hover:border-brand-red">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
              {p.featured && (
                <input
                  data-testid={`caption-job-photo-${p.id}`}
                  defaultValue={p.caption || ""}
                  placeholder="Caption…"
                  maxLength={120}
                  onBlur={(e) => saveCaption(p, e.target.value.trim())}
                  className="mt-1 w-full bg-black border border-zinc-800 px-1.5 py-1 text-[10px] text-zinc-300 focus:border-brand-red outline-none"
                />
              )}
            </div>
          ))}
        </div>
      )}
      {photos.some((p) => p.featured) && (
        <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-yellow-500/70">★ Starred photos appear on the public "Real work" wall</p>
      )}
    </div>
  );
}

export function JobPhotosButton({ appt }) {
  const [open, setOpen] = useState(false);
  const count = (appt.job_photos || []).length;
  return (
    <>
      <button
        data-testid={`job-photos-btn-${appt.id}`}
        onClick={() => setOpen(true)}
        title="Manage before/after photos for this job"
        className={`mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${count ? "border-green-700 text-green-400" : "border-zinc-700 text-zinc-400 hover:border-brand-red hover:text-brand-red"}`}
      >
        <Camera className="h-3 w-3" /> Photos{count ? ` (${count})` : ""}
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 p-6" onClick={(e) => e.stopPropagation()} data-testid={`job-photos-modal-${appt.id}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display uppercase text-lg text-white tracking-tight">Job photos — {appt.user_name}</h3>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <JobPhotosManager appt={appt} />
            <p className="mt-4 text-[11px] text-zinc-600">Photos appear in the customer's logbook and their service-history PDF.</p>
          </div>
        </div>
      )}
    </>
  );
}
